import React from 'react';
import { Redirect } from 'react-router-dom';
import { isBlank } from 'utils';
import { validateBookingBuilderParams, parseBookingBuilderQueryParams } from 'containers/HotelContainer/urlParamsUtils';

/**
 * Higher-Order Component that validates booking builder query parameters
 * before rendering the wrapped component. Redirects to /filters if validation fails.
 *
 * This prevents render errors from invalid query parameters by validating
 * before any component rendering occurs.
 */
export const withBookingBuilderQueryParamValidation = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
  const WithQueryParamValidationComponent: React.FC<P> = props => {
    // Perform validation synchronously during render
    const validateQueryParams = () => {
      // Check if we have any query parameters at all
      if (isBlank(window.location.search)) {
        console.warn('No query parameters found, redirecting to filters');
        return false;
      }

      // Parse and validate query parameters
      const parsedParams = parseBookingBuilderQueryParams(window.location.search);
      const validation = validateBookingBuilderParams(parsedParams);

      if (!validation.isValid) {
        console.warn('Invalid query parameters detected, redirecting to filters:', validation.errors);
        return false;
      }

      return true;
    };

    // Validate synchronously
    const isValid = validateQueryParams();
    
    // If validation fails, redirect to /filters using React Router's Redirect
    if (!isValid) {
      return <Redirect to="/filters" />;
    }

    // Validation passed, render the wrapped component
    return <WrappedComponent {...props} />;
  };

  // Set display name for debugging
  WithQueryParamValidationComponent.displayName = `withQueryParamValidation(${WrappedComponent.displayName ||
    WrappedComponent.name})`;

  return WithQueryParamValidationComponent;
};

export default withBookingBuilderQueryParamValidation;
